import { isAxiosError } from 'axios'
import type { AuthedAxiosRequests } from 'features/auth'
import {
  DOCUMENT_FAVORITES_TYPE,
  type DocumentFavoritesData,
  documentFavoritesDataSchema,
  documentFavoritesEntryId,
  type FavoritesScope,
} from 'features/knowledgeDocumentViewer/utils/documentFavoritesState'
import { z } from 'zod'

const entrySchema = z.object({
  id: z.string(),
  type: z.literal(DOCUMENT_FAVORITES_TYPE),
  owned_by: z.string().nullable().optional(),
  generation: z.number().int().positive(),
  tags: z.object({ site: z.string(), organization: z.string() }),
  data: documentFavoritesDataSchema,
})

export type DocumentFavoritesEntry = z.infer<typeof entrySchema>
export type FavoritesApi = ReturnType<typeof createDocumentFavoritesApi>

export function validateFavoritesEntry(raw: unknown, scope: FavoritesScope) {
  const entry = entrySchema.parse(raw)
  if (
    entry.id !== documentFavoritesEntryId(scope.userId, scope.siteId) ||
    entry.data.user_id !== scope.userId ||
    entry.tags.site !== scope.siteId ||
    entry.tags.organization !== scope.orgId ||
    (entry.owned_by !== null &&
      entry.owned_by !== undefined &&
      entry.owned_by !== scope.userId)
  ) {
    throw new Error('Document favorites scope mismatch')
  }
  return entry
}

export function isFavoritesConflict(error: unknown) {
  return isAxiosError(error) && error.response?.status === 412
}

export function createDocumentFavoritesApi(
  url: string,
  scope: FavoritesScope,
  getAxios: () => AuthedAxiosRequests
) {
  const id = documentFavoritesEntryId(scope.userId, scope.siteId)
  const load = async (): Promise<DocumentFavoritesEntry | null> => {
    const response = await getAxios().post(`${url}/api/v2/entries/load`, {
      ids: [id],
      type: DOCUMENT_FAVORITES_TYPE,
      limit: 1,
    })
    const entries = z
      .array(z.object({ entry: z.unknown() }))
      .max(1)
      .parse(response.data)
    return entries.length
      ? validateFavoritesEntry(entries[0].entry, scope)
      : null
  }
  return {
    load,
    save: async (
      data: DocumentFavoritesData,
      generation: number
    ): Promise<DocumentFavoritesEntry> => {
      await getAxios().put(`${url}/api/v2/entries/`, [
        {
          id,
          type: DOCUMENT_FAVORITES_TYPE,
          owned_by: scope.userId,
          tags: { site: scope.siteId, organization: scope.orgId },
          data,
          if_generation_match: generation,
        },
      ])
      // PUT acknowledges the write; load the entry to get its server generation.
      const saved = await load()
      if (!saved) throw new Error('Document favorites save was not confirmed')
      return saved
    },
  }
}
